import { LOGO_DATA_URI } from '../Logo';
import type { Pedido, ProductoWOP } from '../types';

// Generación del "Formulario de Orden de Pedido / Retiro bajo Contrato".
//
// Vive fuera del componente porque hay dos consumidores del mismo documento:
// el módulo interno (Pedidos.tsx) y la página pública de verificación a la
// que lleva el QR (VerificarPedido.tsx). Una sola fuente de verdad evita que
// el papel y la copia digital se separen con el tiempo.


// Mismo truco que en Contratos.tsx: escribir el tag de apertura de "style"
// literal dentro de un template literal hace que el formateador de código lo
// interprete como JSX y corrompa el archivo. Se arma por concatenación.
const LT = '<';
const styleTag = (css: string) => `${LT}style>${css}${LT}/style>`;

// ── DATOS DEL EMISOR ────────────────────────────────────────────────────────
// Encabezado del formulario oficial: la empresa que COLOCA el pedido.
// Solo falta el RNC (ver abajo).
// Los campos vacíos se imprimen como línea en blanco para llenar a mano.
const EMPRESA = {
  nombre: 'JUNQUITO GAS, S.R.L.',
  // >>> FALTA EL RNC. Mientras esté vacío se imprime una línea en blanco
  // para llenarla a mano. <<<
  rnc: ' 1-30-16152-6',
  direccion:
  'Av. Antonio Guzmán #102, Edificio Don Paso Torres, Bella Vista, ' +
  'Santiago de los Caballeros, Dominican Republic',
  telefono: '(809) 247-2104',
  email: 'info.junquitogas@gmail.com'
};

// Destinatario fijo del formulario (el suplidor bajo contrato).
export const DESTINATARIO = 'Refinería Dominicana de Petróleos, S.A.';

// ── PRODUCTOS ───────────────────────────────────────────────────────────────
// Columnas del formato WOP, en el mismo orden que el formulario impreso.
export const PRODUCTOS_WOP: {id: ProductoWOP;label: string;}[] = [
{ id: 'jetA1', label: 'Jet A-1 / Kerosen' },
{ id: 'gasolinaRegular', label: 'Gasolina Regular' },
{ id: 'gasolinaPremium', label: 'Gasolina Premium' },
{ id: 'gasoilRegular', label: 'Gasoil Regular' },
{ id: 'gasoilOptimo', label: 'Gasoil Óptimo' },
{ id: 'fuelOilReg', label: 'Fuel Oil Reg.' },
{ id: 'fuelOilPrem', label: 'Fuel Oil Prem.' }];


export const COMPARTIMIENTOS = [1, 2, 3, 4, 5, 6];

export const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);

export const fmtFecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};



const esc = (s: string) =>
(s ?? '').
toString().
replace(/&/g, '&amp;').
replace(/</g, '&lt;').
replace(/>/g, '&gt;').
replace(/"/g, '&quot;');

// Abre una ventana nueva con el documento ya armado y lanza la impresión.
function openPrintWindow(html: string) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('El navegador bloqueó la ventana. Habilita las ventanas emergentes.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

// ── GENERADOR DE QR ─────────────────────────────────────────────────────────
// Escrito a mano y dentro de este mismo archivo, a propósito:
//
//   · Magic Patterns no instala dependencias de npm, así que un import de
//     una librería de QR rompe su build.
//   · Estaba en su propio módulo './qr', pero el proyecto se mantiene
//     copiando archivos sueltos a Magic Patterns y era fácil pegar este y
//     olvidar el otro, lo que deja el build roto. Con todo junto, este
//     archivo se copia solo y funciona.
//
// Alcance reducido a lo que el sistema necesita:
//   · Modo byte (8 bits), que cubre cualquier URL.
//   · Corrección de errores nivel M (~15%), la adecuada para papel: aguanta
//     manchas y dobleces sin inflar el tamaño del código.
//   · Versiones 1 a 10, o sea hasta 213 bytes. Las URLs de verificación
//     rondan los 80 caracteres, así que sobra margen.
//
// Implementa la norma ISO/IEC 18004.

// ── Aritmética en GF(256) ───────────────────────────────────────────────────
// Campo de Galois con polinomio primitivo 0x11D, base de Reed-Solomon.

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(function inicializarCampo() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  // Se duplica la tabla para poder sumar exponentes sin hacer módulo.
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

// Polinomio generador de grado `grado`: producto de (x + α^i).
function polinomioGenerador(grado: number): number[] {
  let g = [1];
  for (let i = 0; i < grado; i++) {
    const siguiente = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      siguiente[j] ^= g[j];
      siguiente[j + 1] ^= mul(g[j], EXP[i]);
    }
    g = siguiente;
  }
  return g;
}

// Resto de dividir los datos por el generador: son los códigos de corrección.
function correccion(datos: number[], largoEC: number): number[] {
  const gen = polinomioGenerador(largoEC);
  const resto = datos.concat(new Array(largoEC).fill(0));
  for (let i = 0; i < datos.length; i++) {
    const coef = resto[i];
    if (coef === 0) continue;
    for (let j = 0; j < gen.length; j++) {
      resto[i + j] ^= mul(gen[j], coef);
    }
  }
  return resto.slice(datos.length);
}

// ── Tablas fijas de la norma (nivel M, versiones 1-10) ──────────────────────

// Por versión: códigos de corrección por bloque, y la estructura de bloques
// como [cantidad, códigos de datos por bloque][]. Tabla 9 de la norma.
const BLOQUES_M: {ec: number;grupos: [number, number][];}[] = [
{ ec: 10, grupos: [[1, 16]] }, // v1  → 16 bytes
{ ec: 16, grupos: [[1, 28]] }, // v2  → 28
{ ec: 26, grupos: [[1, 44]] }, // v3  → 44
{ ec: 18, grupos: [[2, 32]] }, // v4  → 64
{ ec: 24, grupos: [[2, 43]] }, // v5  → 86
{ ec: 16, grupos: [[4, 27]] }, // v6  → 108
{ ec: 18, grupos: [[4, 31]] }, // v7  → 124
{ ec: 22, grupos: [[2, 38], [2, 39]] }, // v8  → 154
{ ec: 22, grupos: [[3, 36], [2, 37]] }, // v9  → 182
{ ec: 26, grupos: [[4, 43], [1, 44]] } // v10 → 216
];


// Centros de los patrones de alineación por versión.
const ALINEACION: number[][] = [
[], // v1 no lleva
[6, 18],
[6, 22],
[6, 26],
[6, 30],
[6, 34],
[6, 22, 38],
[6, 24, 42],
[6, 26, 46],
[6, 28, 50]];


// Información de formato ya calculada (BCH + máscara 0x5412), nivel M,
// indexada por patrón de máscara.
const FORMATO_M = [
0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0];


// Información de versión (solo aplica de la v7 en adelante).
const INFO_VERSION: Record<number, number> = {
  7: 0x07c94,
  8: 0x085bc,
  9: 0x09a99,
  10: 0x0a4d3
};

// Las 8 máscaras de la norma. Devuelven true donde hay que invertir.
const MASCARAS: ((f: number, c: number) => boolean)[] = [
(f, c) => (f + c) % 2 === 0,
(f) => f % 2 === 0,
(_f, c) => c % 3 === 0,
(f, c) => (f + c) % 3 === 0,
(f, c) => (Math.floor(f / 2) + Math.floor(c / 3)) % 2 === 0,
(f, c) => f * c % 2 + f * c % 3 === 0,
(f, c) => (f * c % 2 + f * c % 3) % 2 === 0,
(f, c) => ((f + c) % 2 + f * c % 3) % 2 === 0];


function datosPorVersion(version: number): number {
  return BLOQUES_M[version - 1].grupos.reduce(
    (suma, [cantidad, largo]) => suma + cantidad * largo,
    0
  );
}

// ── Codificación de los datos ───────────────────────────────────────────────

function aBytes(texto: string): number[] {
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(texto));
  }
  // Respaldo para entornos sin TextEncoder: UTF-8 a mano.
  const salida: number[] = [];
  for (const ch of texto) {
    const cp = ch.codePointAt(0) as number;
    if (cp < 0x80) salida.push(cp);else
    if (cp < 0x800) {
      salida.push(0xc0 | cp >> 6, 0x80 | cp & 0x3f);
    } else if (cp < 0x10000) {
      salida.push(0xe0 | cp >> 12, 0x80 | cp >> 6 & 0x3f, 0x80 | cp & 0x3f);
    } else {
      salida.push(
        0xf0 | cp >> 18,
        0x80 | cp >> 12 & 0x3f,
        0x80 | cp >> 6 & 0x3f,
        0x80 | cp & 0x3f
      );
    }
  }
  return salida;
}

// Arma el flujo de códigos de datos: cabecera, contenido, relleno.
function codificar(bytes: number[], version: number): number[] {
  // El indicador de cantidad ocupa 8 bits hasta la v9 y 16 desde la v10.
  const bitsCuenta = version < 10 ? 8 : 16;
  const totalDatos = datosPorVersion(version);

  const bits: number[] = [];
  const empujar = (valor: number, largo: number) => {
    for (let i = largo - 1; i >= 0; i--) bits.push(valor >> i & 1);
  };

  empujar(0b0100, 4); // modo byte
  empujar(bytes.length, bitsCuenta);
  for (const b of bytes) empujar(b, 8);

  // Terminador: hasta 4 ceros, sin pasarse de la capacidad.
  const capacidad = totalDatos * 8;
  for (let i = 0; i < 4 && bits.length < capacidad; i++) bits.push(0);
  // Completar el último byte.
  while (bits.length % 8 !== 0) bits.push(0);

  const codigos: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = byte << 1 | bits[i + j];
    codigos.push(byte);
  }
  // Relleno alternando 0xEC y 0x11, como manda la norma.
  const RELLENO = [0xec, 0x11];
  let k = 0;
  while (codigos.length < totalDatos) codigos.push(RELLENO[k++ % 2]);

  return codigos;
}

// Reparte en bloques, calcula la corrección de cada uno y los intercala.
function intercalar(codigos: number[], version: number): number[] {
  const { ec: largoEC, grupos } = BLOQUES_M[version - 1];

  const bloquesDatos: number[][] = [];
  const bloquesEC: number[][] = [];
  let offset = 0;
  for (const [cantidad, largo] of grupos) {
    for (let i = 0; i < cantidad; i++) {
      const datos = codigos.slice(offset, offset + largo);
      offset += largo;
      bloquesDatos.push(datos);
      bloquesEC.push(correccion(datos, largoEC));
    }
  }

  const salida: number[] = [];
  const maxDatos = Math.max(...bloquesDatos.map((b) => b.length));
  for (let i = 0; i < maxDatos; i++) {
    for (const bloque of bloquesDatos) {
      if (i < bloque.length) salida.push(bloque[i]);
    }
  }
  for (let i = 0; i < largoEC; i++) {
    for (const bloque of bloquesEC) salida.push(bloque[i]);
  }
  return salida;
}

// ── Construcción de la matriz ───────────────────────────────────────────────

type Matriz = (boolean | null)[][];

function patronBusqueda(m: Matriz, fila: number, col: number, lado: number) {
  // El rango -1..7 pinta también el separador blanco alrededor.
  for (let r = -1; r <= 7; r++) {
    if (fila + r < 0 || fila + r >= lado) continue;
    for (let c = -1; c <= 7; c++) {
      if (col + c < 0 || col + c >= lado) continue;
      const borde = r >= 0 && r <= 6 && (c === 0 || c === 6);
      const bordeH = c >= 0 && c <= 6 && (r === 0 || r === 6);
      const centro = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      m[fila + r][col + c] = borde || bordeH || centro;
    }
  }
}

function patronesAlineacion(m: Matriz, version: number) {
  const pos = ALINEACION[version - 1];
  for (const fila of pos) {
    for (const col of pos) {
      // Donde ya hay patrón de búsqueda no se dibuja.
      if (m[fila][col] !== null) continue;
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          m[fila + r][col + c] =
          r === -2 || r === 2 || c === -2 || c === 2 || r === 0 && c === 0;
        }
      }
    }
  }
}

function patronesTiempo(m: Matriz, lado: number) {
  for (let r = 8; r < lado - 8; r++) {
    if (m[r][6] === null) m[r][6] = r % 2 === 0;
  }
  for (let c = 8; c < lado - 8; c++) {
    if (m[6][c] === null) m[6][c] = c % 2 === 0;
  }
}

function infoFormato(m: Matriz, lado: number, mascara: number) {
  const bits = FORMATO_M[mascara];
  for (let i = 0; i < 15; i++) {
    const bit = (bits >> i & 1) === 1;
    // Copia vertical, junto al patrón superior izquierdo y el inferior.
    if (i < 6) m[i][8] = bit;else
    if (i < 8) m[i + 1][8] = bit;else
    m[lado - 15 + i][8] = bit;
    // Copia horizontal.
    if (i < 8) m[8][lado - i - 1] = bit;else
    if (i < 9) m[8][15 - i] = bit;else
    m[8][14 - i] = bit;
  }
  // Módulo siempre oscuro que exige la norma.
  m[lado - 8][8] = true;
}

function infoVersion(m: Matriz, lado: number, version: number) {
  if (version < 7) return;
  const bits = INFO_VERSION[version];
  for (let i = 0; i < 18; i++) {
    const bit = (bits >> i & 1) === 1;
    m[Math.floor(i / 3)][i % 3 + lado - 11] = bit;
    m[i % 3 + lado - 11][Math.floor(i / 3)] = bit;
  }
}

// Recorrido en zigzag de abajo a la derecha hacia arriba, en pares de
// columnas, saltando la columna 6 (patrón de tiempo). La máscara se aplica
// aquí mismo, que es como lo plantea la norma.
function colocarDatos(
m: Matriz,
lado: number,
datos: number[],
mascara: number)
{
  const invertir = MASCARAS[mascara];
  let inc = -1;
  let fila = lado - 1;
  let bit = 7;
  let byte = 0;

  for (let col = lado - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    for (;;) {
      for (let c = 0; c < 2; c++) {
        if (m[fila][col - c] !== null) continue;
        let oscuro = false;
        if (byte < datos.length) {
          oscuro = (datos[byte] >>> bit & 1) === 1;
        }
        if (invertir(fila, col - c)) oscuro = !oscuro;
        m[fila][col - c] = oscuro;
        bit--;
        if (bit === -1) {
          byte++;
          bit = 7;
        }
      }
      fila += inc;
      if (fila < 0 || fila >= lado) {
        fila -= inc;
        inc = -inc;
        break;
      }
    }
  }
}

// Penalización de la norma: se prueban las 8 máscaras y gana la de menor
// puntaje, que es la que deja el código más fácil de leer.
function penalizacion(m: boolean[][], lado: number): number {
  let total = 0;

  // Regla 1: rachas de 5 o más módulos iguales, por filas y por columnas.
  for (let i = 0; i < lado; i++) {
    for (const porFila of [true, false]) {
      let racha = 1;
      for (let j = 1; j < lado; j++) {
        const actual = porFila ? m[i][j] : m[j][i];
        const previo = porFila ? m[i][j - 1] : m[j - 1][i];
        if (actual === previo) {
          racha++;
        } else {
          if (racha >= 5) total += racha - 2;
          racha = 1;
        }
      }
      if (racha >= 5) total += racha - 2;
    }
  }

  // Regla 2: bloques de 2×2 del mismo color.
  for (let f = 0; f < lado - 1; f++) {
    for (let c = 0; c < lado - 1; c++) {
      const v = m[f][c];
      if (m[f][c + 1] === v && m[f + 1][c] === v && m[f + 1][c + 1] === v) {
        total += 3;
      }
    }
  }

  // Regla 3: el patrón 1:1:3:1:1 con 4 claros a un lado, que se confunde
  // con un patrón de búsqueda.
  const PATRON = [true, false, true, true, true, false, true];
  const claros = [false, false, false, false];
  const buscar = (linea: boolean[]) => {
    for (let i = 0; i + 7 <= linea.length; i++) {
      const trozo = linea.slice(i, i + 7);
      if (!PATRON.every((v, k) => v === trozo[k])) continue;
      const antes = linea.slice(Math.max(0, i - 4), i);
      const despues = linea.slice(i + 7, i + 11);
      if (
      antes.length === 4 && claros.every((v, k) => v === antes[k]) ||
      despues.length === 4 && claros.every((v, k) => v === despues[k]))
      {
        total += 40;
      }
    }
  };
  for (let i = 0; i < lado; i++) {
    buscar(m[i]);
    buscar(m.map((f) => f[i]));
  }

  // Regla 4: desbalance entre módulos oscuros y claros.
  let oscuros = 0;
  for (let f = 0; f < lado; f++) {
    for (let c = 0; c < lado; c++) if (m[f][c]) oscuros++;
  }
  const porcentaje = oscuros * 100 / (lado * lado);
  total += Math.floor(Math.abs(porcentaje - 50) / 5) * 10;

  return total;
}

function construir(
datos: number[],
version: number,
mascara: number)
: boolean[][] {
  const lado = 17 + 4 * version;
  const m: Matriz = Array.from({ length: lado }, () =>
  new Array(lado).fill(null)
  );

  patronBusqueda(m, 0, 0, lado);
  patronBusqueda(m, lado - 7, 0, lado);
  patronBusqueda(m, 0, lado - 7, lado);
  patronesAlineacion(m, version);
  patronesTiempo(m, lado);
  infoFormato(m, lado, mascara);
  infoVersion(m, lado, version);
  colocarDatos(m, lado, datos, mascara);

  return m.map((fila) => fila.map((v) => v === true));
}

// ── API pública ─────────────────────────────────────────────────────────────

/**
 * Codifica un texto y devuelve la matriz de módulos del QR.
 * `true` = módulo oscuro. La zona de silencio del borde NO va incluida:
 * la agrega quien dibuja.
 */
function matrizQR(texto: string): boolean[][] {
  const bytes = aBytes(texto);

  // Versión más chica que alcance. El cálculo de bits tiene que contemplar
  // que a partir de la v10 el indicador de cantidad pasa de 8 a 16 bits.
  let version = 0;
  for (let v = 1; v <= 10; v++) {
    const bitsCuenta = v < 10 ? 8 : 16;
    const necesarios = 4 + bitsCuenta + bytes.length * 8;
    if (necesarios <= datosPorVersion(v) * 8) {
      version = v;
      break;
    }
  }
  if (version === 0) {
    throw new Error(
      `El texto no cabe en un QR de hasta versión 10 (${bytes.length} bytes).`
    );
  }

  const datos = intercalar(codificar(bytes, version), version);

  // Se arman las 8 máscaras y gana la de menor penalización.
  let mejor: boolean[][] | null = null;
  let mejorPuntaje = Infinity;
  const lado = 17 + 4 * version;
  for (let mascara = 0; mascara < 8; mascara++) {
    const candidata = construir(datos, version, mascara);
    const puntaje = penalizacion(candidata, lado);
    if (puntaje < mejorPuntaje) {
      mejorPuntaje = puntaje;
      mejor = candidata;
    }
  }
  return mejor as boolean[][];
}

// ── VERIFICACIÓN POR QR ─────────────────────────────────────────────────────

// Token impredecible que se acuña al autorizar. No se usa el id ni el número
// de orden porque ambos son adivinables y permitirían enumerar pedidos.
export function nuevoToken(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes).
  map((b) => b.toString(16).padStart(2, '0')).
  join('');
}

// URL absoluta de la copia digital. Se arma desde window.location para que
// apunte sola al dominio donde esté publicada la app, sin configurar nada.
export function urlVerificacion(token: string): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname.replace(/\/$/, '')}/?verificar=${token}`;
}

// QR como SVG en línea, no como <img src="data:...">. Dos razones:
//
// 1. Un data URI depende de que la política de contenido de la página
//    permita `img-src data:`. El preview de Magic Patterns corre la app
//    dentro de un iframe cuya CSP la bloquea, y el QR salía en blanco.
//    Un SVG incrustado es parte del documento: ninguna CSP lo filtra.
// 2. Es vectorial, así que imprime nítido a cualquier tamaño; el GIF se
//    pixelaba al ampliarlo.
//
// El tag de apertura se arma con LT por el mismo motivo que styleTag: un
// `<svg` literal dentro de un template literal confunde al formateador.
function qrSvg(texto: string): string {
  const modulos = matrizQR(texto);
  const n = modulos.length;
  const margen = 2; // "quiet zone" que exige el estándar para que se lea
  const lado = n + margen * 2;
  // Un solo path con un cuadrito por módulo oscuro: mucho más liviano que
  // un <rect> por módulo (son ~1.100 en un QR de este tamaño).
  let d = '';
  for (let fila = 0; fila < n; fila++) {
    for (let col = 0; col < n; col++) {
      if (modulos[fila][col]) {
        d += `M${col + margen} ${fila + margen}h1v1h-1z`;
      }
    }
  }
  return (
    `${LT}svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${lado} ${lado}" ` +
    `shape-rendering="crispEdges" role="img" aria-label="Código de verificación">` +
    `${LT}rect width="${lado}" height="${lado}" fill="#fff"/>` +
    `${LT}path d="${d}" fill="#000"/>` +
    `${LT}/svg>`);

}

// ── DOCUMENTO IMPRIMIBLE ────────────────────────────────────────────────────
// Vive fuera del componente porque la página pública de verificación imprime
// exactamente el mismo formulario: una sola fuente de verdad del documento.

export function construirFormularioHTML(p: Pedido): string {
  const css = `
    @page { size: letter; margin: 1.6cm 1.8cm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
    body { font-family: 'Arial', sans-serif; font-size: 10.5pt; color: #000; line-height: 1.35; }
    .letterhead { display: flex; align-items: center; gap: 18px; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 6px; }
    .letterhead img { height: 118px; width: auto; flex-shrink: 0; }
    .company { flex: 1; text-align: center; }
    .company .name { font-size: 13pt; font-weight: bold; text-transform: uppercase; }
    .company .line { font-size: 9pt; line-height: 1.25; }
    .company .addr { max-width: 12.5cm; margin: 0 auto; }
    .blank { display: inline-block; min-width: 180px; border-bottom: 1px solid #000; }
    .doc-title { text-align: center; font-weight: bold; font-size: 12pt; text-transform: uppercase; margin: 12px 0 18px; }
    .meta { margin-bottom: 4px; }
    .meta strong { text-transform: uppercase; }
    .to { margin: 14px 0 10px; }
    .intro { text-align: justify; margin-bottom: 16px; }
    .truck { margin-bottom: 4px; }
    .truck .gap { display: inline-block; width: 34px; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0 22px; }
    th, td { border: 1px solid #000; padding: 5px 4px; font-size: 8.5pt; text-align: center; vertical-align: middle; }
    th { font-weight: bold; }
    td.dest { text-align: left; font-size: 9pt; padding-left: 6px; }
    td.compart { text-align: left; font-weight: bold; padding-left: 6px; white-space: nowrap; }
    td.num { text-align: right; padding-right: 6px; font-variant-numeric: tabular-nums; }
    .glp-table th { font-size: 9.5pt; }
    .glp-table td { font-size: 11pt; padding: 10px 6px; }
    .foot { margin-top: 34px; display: flex; align-items: flex-end; justify-content: space-between; gap: 30px; }
    .auth .name { border-bottom: 1px solid #000; display: inline-block; min-width: 260px; padding-bottom: 2px; }
    .auth .cargo { margin-top: 9px; font-size: 9.5pt; }
    .qr { text-align: center; flex: 0 0 auto; }
    .qr svg { width: 2.7cm; height: 2.7cm; display: block; }
    .qr .cap { font-size: 7pt; margin-top: 3px; line-height: 1.2; max-width: 2.9cm; }
    .qr .code { font-family: monospace; font-size: 6pt; word-break: break-all; }
    .void { position: fixed; top: 42%; left: 12%; font-size: 62pt; color: rgba(220,38,38,.22); font-weight: bold; transform: rotate(-22deg); letter-spacing: 6px; }
    @media print { .no-print { display: none; } }
    .no-print { position: fixed; top: 14px; right: 14px; }
    .no-print button {
      background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
      border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
      font-family: sans-serif; box-shadow: 0 6px 18px rgba(255,107,1,.35);
    }
  `;

  // LOGO_DATA_URI es una ruta relativa ('/pasted-image.png'). La ventana de
  // impresión se abre como about:blank y no siempre hereda la base del
  // documento que la abrió, así que se resuelve a URL absoluta antes de
  // incrustarla o el logo sale roto.
  const logoSrc = new URL(LOGO_DATA_URI, window.location.href).href;

  // El dato va en texto normal: la negrita es solo de la etiqueta, para que
  // se distinga qué se pregunta de qué se respondió.
  const campo = (valor: string, ancho = 180) =>
  valor?.trim() ?
  esc(valor) :
  `<span class="blank" style="min-width:${ancho}px"></span>`;

  // Tabla WOP: una fila por compartimiento y una columna por producto; los
  // galones caen en la columna del producto elegido.
  const tablaWOP = () => {
    const heads = PRODUCTOS_WOP.map((prod) => `<th>${esc(prod.label)}</th>`).join(
      ''
    );
    const filas = COMPARTIMIENTOS.map((n) => {
      const linea = p.lineas.find((l) => l.compartimiento === n);
      const celdas = PRODUCTOS_WOP.map((prod) =>
      linea && linea.producto === prod.id && linea.galones > 0 ?
      `<td class="num">${fmtGal(linea.galones)}</td>` :
      '<td></td>'
      ).join('');
      return `<tr>
        <td class="compart">Compart. ${n}</td>
        ${celdas}
        <td class="dest">${esc(linea?.destino ?? '')}</td>
        <td class="dest">${esc(linea?.rncDestino ?? '')}</td>
      </tr>`;
    }).join('');
    return `<table>
      <thead>
        <tr><th>COMPART.</th>${heads}<th>DESTINO</th><th>RNC del Destino</th></tr>
      </thead>
      <tbody>${filas}</tbody>
    </table>`;
  };

  // Tabla GLP: una sola línea con el total de galones.
  const tablaGLP = () => {
    const filas = p.lineas.
    map(
      (l) => `<tr>
        <td class="num">${fmtGal(l.galones)}</td>
        <td class="dest">${esc(l.destino)}</td>
        <td class="dest">${esc(l.rncDestino)}</td>
      </tr>`
    ).
    join('');
    return `<table class="glp-table">
      <thead>
        <tr>
          <th>CANTIDAD DE GALONES GLP</th>
          <th>DESTINO PEDIDO</th>
          <th>RNC DESTINO</th>
        </tr>
      </thead>
      <tbody>${filas}</tbody>
    </table>`;
  };

  // El QR solo existe una vez autorizado el pedido: es la prueba de que el
  // documento impreso corresponde a una orden firmada, y lleva a la copia
  // digital para que quien lo recibe pueda cotejarla.
  const bloqueQR = () => {
    if (!p.token) return '';
    const url = urlVerificacion(p.token);
    return `<div class="qr">
      ${qrSvg(url)}
      <div class="cap">Escanea para ver la copia digital</div>
      <div class="cap code">${esc(p.token.slice(0, 12))}</div>
    </div>`;
  };

  const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Orden de Pedido ${esc(p.numeroOrden)} - ${esc(p.tipo)}</title>
${styleTag(css)}
</head>`;

  const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  ${p.estado === 'anulado' ? '<div class="void">ANULADO</div>' : ''}

  <div class="letterhead">
    <img src="${logoSrc}" alt="${esc(EMPRESA.nombre)}" />
    <div class="company">
      <div class="name">${esc(EMPRESA.nombre)}</div>
      <div class="line">RNC# ${campo(EMPRESA.rnc, 140)}</div>
      <div class="line addr">${esc(EMPRESA.direccion) || '<span class="blank" style="min-width:340px"></span>'}</div>
      <div class="line">Teléfono de Contacto: ${campo(EMPRESA.telefono, 140)}${
      EMPRESA.email ? ` &nbsp;·&nbsp; ${esc(EMPRESA.email)}` : ''}</div>
    </div>
  </div>

  <div class="doc-title">Formulario de Orden de Pedido / Retiro bajo Contrato</div>

  <div class="meta"><strong>Fecha:</strong> ${esc(fmtFecha(p.fecha))}</div>
  <div class="meta"><strong>Orden de Pedido Número:</strong> ${esc(p.numeroOrden)}</div>

  <div class="to"><strong>Señores:</strong> &nbsp;&nbsp;&nbsp;&nbsp;${esc(DESTINATARIO)}</div>

  <div class="intro">
    Por la presente autorizamos el despacho de productos, contra nuestro contrato
    vigente, y de acuerdo al detalle que se consigna más abajo en cantidades
    medidas en Galones Americanos (AG):
  </div>

  <div class="truck">
    <strong>CONDUCTOR DEL CAMION:</strong> ${campo(p.conductor, 220)}
    <span class="gap"></span>
    <strong>Pase del Chofer:</strong> ${campo(p.paseChofer, 110)}
  </div>
  <div class="truck">
    <strong>FICHA DE LA COLA:</strong> ${campo(p.fichaCola, 110)}
    <span class="gap"></span>
    <strong>Placa del Camión:</strong> ${campo(p.placaCamion, 140)}
  </div>

  ${p.tipo === 'WOP' ? tablaWOP() : tablaGLP()}

  <div class="foot">
    <div class="auth">
      <strong>AUTORIZADO POR:</strong>
      <span class="name">${esc(p.autorizadoPor)}</span>
      <div class="cargo">${esc(p.autorizadoCargo)}</div>
    </div>
    ${bloqueQR()}
  </div>

  ${p.observaciones?.trim() ?
  `<div style="margin-top:20px;font-size:9.5pt"><strong>Observaciones:</strong> ${esc(p.observaciones)}</div>` :
  ''}
</body>
</html>`;

  return head + body;
}

// Abre el formulario en una pestaña nueva y lanza el diálogo de impresión.
export function imprimirPedido(p: Pedido) {
  openPrintWindow(construirFormularioHTML(p));
}
